import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import {
  AllReaOverviewResponse,
  ReaContractAnalysisDetail,
  ReaMatchingDetail,
  ReaReport,
  ReaReportBulkFetchResult,
  ReaReportSourceItem,
} from '../../../core/models/domain.models';

@Injectable({ providedIn: 'root' })
export class ReaReportsService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.apiBaseUrl;
  private readonly defaultRpcRegion: RpcRegion = 'SRPC';

  private buildUrl(path: string): string {
    const base = this.baseUrl.replace(/\/+$/, '');
    return `${base}${path.startsWith('/') ? path : `/${path}`}`;
  }

  listSource(rpcRegion: RpcRegion = this.defaultRpcRegion) {
    return this.http.get<ReaReportSourceItem[]>(this.buildUrl('/api/rea-reports/source'), {
      withCredentials: true,
      params: { rpc_region: rpcRegion },
    });
  }

  listSaved(rpcRegion: RpcRegion = this.defaultRpcRegion) {
    return this.http.get<ReaReport[]>(this.buildUrl('/api/rea-reports'), {
      withCredentials: true,
      params: { rpc_region: rpcRegion },
    });
  }

  getAllReaOverview(filters?: {
    rpcRegion?: string | null;
    month?: number | '' | null;
    year?: number | '' | null;
    search?: string | null;
    page?: number | null;
    pageSize?: number | null;
  }) {
    const params: Record<string, string> = {};
    if (filters?.rpcRegion) {
      params['rpc_region'] = filters.rpcRegion;
    }
    if (filters?.month) {
      params['month'] = String(filters.month);
    }
    if (filters?.year) {
      params['year'] = String(filters.year);
    }
    if (filters?.search) {
      params['search'] = filters.search;
    }
    if (filters?.page) {
      params['page'] = String(filters.page);
    }
    if (filters?.pageSize) {
      params['page_size'] = String(filters.pageSize);
    }
    return this.http.get<AllReaOverviewResponse>(this.buildUrl('/api/rea-reports/all-rea'), {
      withCredentials: true,
      params,
    });
  }

  fetch(payload: {
    source_url: string;
    title?: string | null;
    report_month?: number | null;
    report_year?: number | null;
    report_type?: string;
    rpc_region?: string;
    force_refetch?: boolean;
  }) {
    return this.http.post<ReaReport>(this.buildUrl('/api/rea-reports/fetch_srpc'), payload, { withCredentials: true });
  }

  autoFetch(filters?: { month?: number | null; year?: number | null; rpcRegion?: RpcRegion }) {
    const params: Record<string, string> = {};
    if (filters?.month) {
      params['month'] = String(filters.month);
    }
    if (filters?.year) {
      params['year'] = String(filters.year);
    }
    params['rpc_region'] = filters?.rpcRegion || this.defaultRpcRegion;
    return this.http.post<ReaReportBulkFetchResult>(this.buildUrl('/api/rea-reports/auto-fetch'), {}, { withCredentials: true, params });
  }

  refetch(reportId: string) {
    return this.http.post<ReaReport>(this.buildUrl(`/api/rea-reports/${reportId}/refetch`), {}, { withCredentials: true });
  }

  getMatching(reportId: string) {
    return this.http.get<ReaMatchingDetail>(this.buildUrl(`/api/rea-reports/${reportId}/matching`), { withCredentials: true });
  }

  getContractAnalysis(reportId: string) {
    return this.http.get<ReaContractAnalysisDetail>(this.buildUrl(`/api/rea-reports/${reportId}/contract-analysis`), {
      withCredentials: true,
    });
  }

  downloadUrl(reportId: string) {
    return this.buildUrl(`/api/rea-reports/${reportId}/download`);
  }
}

type RpcRegion = 'SRPC' | 'NRPC' | 'NERPC' | 'WRPC';
